import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { cleanLegalHtmlForReader } from './legal-html-clean.util';

/**
 * Server-written legal text (terms, privacy, a re-agreement notice) - the ONE place either site puts HTML it did not write itself
 * onto the page (review MR !1 D4, W44).
 *
 * ⚠ ONE REVIEWED BYPASS, AFTER OUR OWN ALLOWLIST. Angular's sanitiser has no `id` / `name`, so a Word table of contents would point at
 * nothing. `cleanLegalHtmlForReader` builds a fresh tree from the server's text, copying only what the server's own cleaner allows, so
 * the single `bypassSecurityTrustHtml` below only ever receives text that has been through both. No other bypass may be added here.
 *
 * ⚠ A `#section` LINK STAYS ON THE PAGE. `index.html` has `<base href="/">`, so a bare `#_Toc1` would resolve to `/#_Toc1` (the site root).
 * Each such href is written as this page's `pathname + search + '#token'` when the text is drawn, so a middle-click or "open in new
 * tab" works; a plain click is handled here (target scrolled to and focused), and a Ctrl/Cmd/Shift/Alt click is left to the browser.
 *
 * ⚠ THE HOST IS THE CONTAINER, with no wrapper inside it. A consumer puts its own class on `<lm-legal-body>` and keeps every rule it
 * had for the old `<div>`; `:host` is a block, which is all a `<div>` was.
 *
 * Shared by the partner portal and the patient site through the `@lm-shared` path alias, so it imports Angular only.
 */
@Component({
    selector: 'lm-legal-body',
    template: '',
    styleUrl: './legal-body.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[innerHTML]': 'safeHtml()', '(click)': 'followFragment($event)' },
})
export class LegalBodyComponent {
    private readonly sanitizer = inject(DomSanitizer);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** The server's HTML. It is cleaned by `cleanLegalHtmlForReader` before it is drawn. */
    readonly html = input<string | null | undefined>('');

    /** What is put in front of each `#fragment` link when the text is drawn: this page's path and query, read again whenever the text changes. */
    private readonly pagePath = computed(() => {
        this.html();
        return `${window.location.pathname}${window.location.search}`;
    });

    /** The cleaned text. The ONE reviewed bypass: the input has just been through the inert-DOM allowlist pass. */
    protected readonly safeHtml = computed<SafeHtml>(() =>
        this.sanitizer.bypassSecurityTrustHtml(cleanLegalHtmlForReader(this.html(), this.pagePath())),
    );

    /** Scrolls to a same-page `#fragment` target inside this text and moves focus there, instead of letting `<base href="/">` send the reader to the site root. */
    protected followFragment(event: MouseEvent): void {
        // A modified click (new tab, new window, download) is the browser's: the href already names this page, so it works unchanged.
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }
        const link = (event.target as Element | null)?.closest?.('a');
        const href = link?.getAttribute('href');
        const hashAt = href ? href.indexOf('#') : -1;
        if (!link || !href || hashAt < 0 || href.slice(0, hashAt) !== this.pagePath() || !this.host.nativeElement.contains(link)) {
            return;
        }
        event.preventDefault();
        const token = href.slice(hashAt + 1);
        const target = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[id], a[name]')).find(
            (element) => element.getAttribute('id') === token || element.getAttribute('name') === token,
        );
        if (!target) {
            return;
        }
        // A heading is not focusable by default; -1 lets a script (and nothing else) put the keyboard and screen-reader position there.
        if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'start' });
    }
}
